<template>
  <Drawer
    :visible="whatsNewStore.panelOpen"
    position="right"
    header="What's new"
    class="tw:!w-full tw:sm:!w-[26rem]"
    @update:visible="visible => !visible && whatsNewStore.closePanel()"
  >
    <p v-if="listed.length === 0" class="tw:text-sm tw:text-slate-400">
      Nothing new right now. Announcements about console changes will show up here.
    </p>

    <ol class="tw:list-none tw:m-0 tw:p-0 tw:flex tw:flex-col tw:gap-4">
      <li
        v-for="item in listed"
        :key="item.id"
        class="tw:rounded-xl tw:border tw:border-slate-800 tw:bg-slate-955 tw:p-4"
      >
        <div class="tw:flex tw:items-center tw:gap-2 tw:mb-1.5">
          <span
            v-if="newWhenOpened.has(item.id)"
            class="tw:rounded-full tw:bg-indigo-600 tw:px-2 tw:py-0.5 tw:text-2xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-white"
          >New</span>
          <time :datetime="item.publishedAt" class="tw:text-xs tw:text-slate-500">{{ formatDate(item.publishedAt) }}</time>
        </div>
        <h3 class="tw:text-base tw:font-semibold tw:text-slate-100 tw:m-0 tw:mb-2">{{ item.title }}</h3>
        <WhatsNewMedia v-if="item.media" :item-id="item.id" :title="item.title" :media="item.media" />
        <p
          v-for="(paragraph, index) in item.body"
          :key="index"
          class="tw:text-sm tw:text-slate-300 tw:m-0 tw:mb-2 tw:last:mb-0"
        >
          {{ paragraph }}
        </p>
        <!-- The plan pitch, in trusted HTML from ROUTE_UPSELLING_DETAILS. -->
        <p
          v-if="upsellFor(item)?.additionalDetails"
          class="tw:flex tw:items-start tw:gap-1.5 tw:text-sm tw:text-slate-300 tw:m-0 tw:mt-3"
        >
          <Gem class="tw:w-4 tw:h-4 tw:mt-0.5 tw:text-indigo-400 tw:shrink-0" aria-hidden="true" />
          <span v-html="upsellFor(item)!.additionalDetails" />
        </p>
        <div
          v-if="upsellFor(item)?.primaryCta || ctaFor(item)"
          class="tw:mt-3 tw:flex tw:flex-wrap tw:items-center tw:gap-2"
        >
          <button
            v-if="upsellFor(item)?.primaryCta"
            type="button"
            class="tw:h-8 tw:px-3 tw:rounded-lg tw:bg-indigo-600 tw:text-xs tw:font-semibold tw:text-white tw:hover:bg-indigo-500 tw:transition-colors tw:cursor-pointer"
            @click="upgrade(item)"
          >
            {{ upsellFor(item)!.primaryCta!.text }}
          </button>
          <!-- Beside an upgrade button, the call to action is the secondary one. -->
          <button
            v-if="ctaFor(item)"
            type="button"
            :class="upsellFor(item)?.primaryCta
              ? 'tw:h-8 tw:px-3 tw:rounded-lg tw:text-xs tw:font-semibold tw:text-indigo-400 tw:hover:bg-indigo-500/10 tw:transition-colors tw:cursor-pointer'
              : 'tw:h-8 tw:px-3 tw:rounded-lg tw:bg-indigo-600 tw:text-xs tw:font-semibold tw:text-white tw:hover:bg-indigo-500 tw:transition-colors tw:cursor-pointer'"
            @click="followCta(item)"
          >
            {{ ctaFor(item)!.label }}
          </button>
        </div>
      </li>
    </ol>

    <!-- Opening the panel marks everything seen, which hides the banner for good. -->
    <button
      v-if="canResetSeen"
      type="button"
      class="tw:mt-6 tw:text-xs tw:text-slate-500 tw:hover:text-slate-300 tw:underline tw:cursor-pointer"
      title="Shown only in development and to staff signed in with sudo"
      @click="resetSeen"
    >
      Reset seen, to show the banner again
    </button>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import Drawer from 'primevue/drawer'
import { Gem } from '@lucide/vue'
import { useWhatsNewStore } from '@/stores/whatsNew'
import { useWhatsNew } from './useWhatsNew'
import { followWhatsNewCta, followWhatsNewUpgrade } from './followCta'
import WhatsNewMedia from './WhatsNewMedia.vue'
import { parseCatalogDate } from './eligibility'
import type { WhatsNewAction, WhatsNewItem } from './whats-new-items'

const emit = defineEmits<{ action: [action: WhatsNewAction] }>()

const router = useRouter()
const store = useStore()
const whatsNewStore = useWhatsNewStore()

const canResetSeen = computed(() => import.meta.env.DEV || store.getters['userProfile/isSudoUser'])
const { listed, unread, ctaFor, upsellFor } = useWhatsNew()

/**
 * What was unread when the panel opened. Opening the panel marks everything in it seen -- the
 * person has the list in front of them -- so the live unread list empties at once, and the "New"
 * pills would vanish while they are still reading.
 */
const newWhenOpened = ref(new Set<string>())

watch(() => whatsNewStore.panelOpen, open => {
  if (open) {
    const ids = unread.value.map(item => item.id)
    newWhenOpened.value = new Set(ids)
    void whatsNewStore.markSeen(ids)
  }
}, { immediate: true })

const dateFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

function formatDate (value: string) {
  return dateFormat.format(parseCatalogDate(value))
}

function followCta (item: WhatsNewItem) {
  const cta = ctaFor(item)
  if (!cta) {
    return
  }
  whatsNewStore.closePanel()
  followWhatsNewCta(router, item.id, cta, 'panel', action => emit('action', action))
}

function upgrade (item: WhatsNewItem) {
  const action = upsellFor(item)?.primaryCta?.action
  if (!action) {
    return
  }
  whatsNewStore.closePanel()
  followWhatsNewUpgrade(item.id, 'panel', action)
}

function resetSeen () {
  // Closed, so the banner can show; open, the panel would mark it all seen again next time.
  whatsNewStore.closePanel()
  void whatsNewStore.resetSeen()
}
</script>
